import SVG from "react-inlinesvg";
import classnames from "classnames";
import { AnimatePresence } from "framer-motion";
import { isArray, isEmpty } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";

import PlusSVG from "@krea/customer-features/images/svg/plus.svg";
import Button from "@krea/customer-features/shared-components/button";
import { Error } from "@krea/customer-features/shared-components/fields/error";
import { FileInput } from "@krea/customer-features/shared-components/fields/file/index";
import FileItem from "@krea/customer-features/shared-components/fields/file-item";
import FilesList from "@krea/customer-features/shared-components/fields/files-list";
import { Label } from "@krea/customer-features/shared-components/fields/label";
import {
  useDeleteFile,
  usePostFieldRequest,
} from "@krea/customer-features/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/customer-features/store/field-requests/hooks/queries";

import FieldRequestPreviewHeader from "../../FieldRequestPreviewHeader";
import FieldRequestSaved from "../../FieldRequestSaved";

import EditRefinancingAmount from "./EditRefinancingAmount";
import NewRefinancingAmount from "./NewRefinancingAmount";
import RefinancingAmountItem from "./RefinancingAmountItem";

const RefinancingAmountForm = ({
  fieldRequestID,
  isPreviewMode,
  t,
  withInputLabel,
}) => {
  const [files, setFiles] = useState(null);
  const [refinancingItems, setRefinancingItems] = useState([]);
  const [newRefinancingItem, setNewRefinancingItem] = useState(null);
  const [isNewRefinancingItemOpen, setIsNewRefinancingItemOpen] =
    useState(true);
  const [isEditRefinancingItemOpen, setIsEditRefinancingItemOpen] =
    useState(false);
  const [[selectedItem, selectedItemIndex], setSelectedItem] = useState([
    null,
    null,
  ]);

  const { data: { displayName, submittedData } = {} } =
    useGetOneFieldRequest(fieldRequestID);
  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);
  const { mutate: deleteFile } = useDeleteFile();

  const errors = error?.data?.errors || [];

  const manageInputChange = useCallback(
    (key, value) => {
      let newValue = value;

      if (key !== "lender_name") {
        newValue = parseFloat(newValue.trim().replace(/\s/g, ""));
        newValue = Number.isInteger(newValue) ? newValue : null;
      } else {
        newValue = newValue.replace(/^\s+/g, "");
      }

      if (isEditRefinancingItemOpen) {
        setSelectedItem((pv) => [{ ...pv[0], [key]: newValue }, pv[1]]);
      } else if (isNewRefinancingItemOpen) {
        setNewRefinancingItem((pv) => ({ ...pv, [key]: newValue }));
      }
    },
    [isEditRefinancingItemOpen, isNewRefinancingItemOpen],
  );

  const onNewRefinancingItemClick = useCallback(() => {
    setIsNewRefinancingItemOpen((pv) => !pv);
    setNewRefinancingItem({
      lender_name: null,
      monthly_cost: null,
      outstanding_amount: null,
    });
  }, []);

  const onEditItemClick = useCallback(
    (index) => {
      if (!isEditRefinancingItemOpen) {
        setSelectedItem([refinancingItems[index], index]);

        return setIsEditRefinancingItemOpen(true);
      }
      setSelectedItem([null, null]);
      setIsEditRefinancingItemOpen(false);
    },
    [isEditRefinancingItemOpen, refinancingItems],
  );

  const onRemoveItem = useCallback(
    (item) => {
      const amounts = refinancingItems.filter(
        (it) => it.lender_name !== item.lender_name,
      );
      postFieldRequest({ refinancing_amounts: amounts });
      setRefinancingItems(amounts);
    },
    [postFieldRequest, refinancingItems],
  );
  const ResetStates = useCallback(() => {
    const data = submittedData?.refinancing_amounts || [];
    const dataFiles = submittedData?.files || [];
    setRefinancingItems(data);
    setIsEditRefinancingItemOpen(false);
    setSelectedItem([null, null]);
    setFiles(dataFiles);
  }, [submittedData]);

  useEffect(() => {
    ResetStates();
  }, [ResetStates]);

  const onSaveClick = useCallback(() => {
    if (isEditRefinancingItemOpen && selectedItem) {
      if (selectedItem.monthly_cost)
        selectedItem.monthly_cost = parseInt(selectedItem.monthly_cost);
      selectedItem.outstanding_amount = parseInt(
        selectedItem.outstanding_amount,
      );
      const items = refinancingItems;
      items[selectedItemIndex] = selectedItem;

      postFieldRequest({ refinancing_amounts: items });
      setRefinancingItems(items);
      onEditItemClick();
    } else if (isNewRefinancingItemOpen) {
      if (newRefinancingItem.monthly_cost)
        newRefinancingItem.monthly_cost = parseInt(
          newRefinancingItem.monthly_cost,
        );
      newRefinancingItem.outstanding_amount = parseInt(
        newRefinancingItem.outstanding_amount,
      );
      const items = [...refinancingItems, newRefinancingItem];

      postFieldRequest({ refinancing_amounts: items });
    }
    setIsNewRefinancingItemOpen(false);
  }, [
    isEditRefinancingItemOpen,
    isNewRefinancingItemOpen,
    newRefinancingItem,
    onEditItemClick,
    postFieldRequest,
    refinancingItems,
    selectedItem,
    selectedItemIndex,
  ]);

  const onDeleteFile = useCallback(
    (fileID) => deleteFile({ fileID, fieldRequestID }),
    [deleteFile, fieldRequestID],
  );

  const onSaveFiles = useCallback(
    (currentFiles) => {
      const payload = {
        files: currentFiles || files,
      };

      if (payload.files && payload.files.length) {
        const formData = new FormData();

        if (isArray(payload.files) && !isEmpty(payload.files)) {
          payload.files.forEach((file) => {
            formData.append("files", file, file.name);
          });
        }
        postFieldRequest(formData);
      }
    },
    [files, postFieldRequest],
  );

  return (
    <>
      {isPreviewMode ? (
        <FieldRequestPreviewHeader
          label={displayName}
          value={
            refinancingItems.length > 0 &&
            refinancingItems.map((item, index) => (
              <div key={index}>
                <RefinancingAmountItem
                  lenderName={item.lender_name}
                  monthlyCost={item.monthly_cost}
                  outstandingAmount={item.outstanding_amount}
                  onEdit={() => onEditItemClick(index)}
                  onRemove={() => onRemoveItem(item)}
                  isPreviewMode={isPreviewMode}
                  t={t}
                />
                {selectedItemIndex === index && !isPreviewMode && (
                  <EditRefinancingAmount
                    errors={errors}
                    inputChange={manageInputChange}
                    isOpen={isEditRefinancingItemOpen}
                    onCloseClick={() => onEditItemClick()}
                    onSaveClick={onSaveClick}
                    t={t}
                    refinancingAmount={selectedItem}
                  />
                )}
              </div>
            ))
          }
        />
      ) : (
        <>
          <form
            className="input-form"
            name="RefinancingAmountsForm"
            onSubmit={(e) => e.preventDefault()}
          >
            <div>
              {withInputLabel && <Label className="mb-2">{displayName}</Label>}
            </div>

            {refinancingItems.length > 0 && (
              <FilesList>
                {refinancingItems.map((item, index) => (
                  <div key={index}>
                    {selectedItemIndex !== index && (
                      <RefinancingAmountItem
                        lenderName={item.lender_name}
                        monthlyCost={item.monthly_cost}
                        outstandingAmount={item.outstanding_amount}
                        onEdit={() => onEditItemClick(index)}
                        onRemove={() => onRemoveItem(item)}
                        isPreviewMode={isPreviewMode}
                        t={t}
                      />
                    )}
                    {selectedItemIndex === index && !isPreviewMode && (
                      <EditRefinancingAmount
                        errors={errors}
                        inputChange={manageInputChange}
                        isOpen={isEditRefinancingItemOpen}
                        onCloseClick={() => onEditItemClick()}
                        onSaveClick={onSaveClick}
                        t={t}
                        refinancingAmount={selectedItem}
                      />
                    )}
                  </div>
                ))}
              </FilesList>
            )}

            <div
              className={classnames("d-flex", {
                "justify-content-end": isNewRefinancingItemOpen,
                "flex-column": !isNewRefinancingItemOpen,
                "border-top mt-4 pt-4": refinancingItems.length > 0,
              })}
            >
              <Button
                variant="text"
                justify="start"
                size="sm"
                className="mb-4 text-left"
                startIcon={
                  <>
                    {!isNewRefinancingItemOpen && (
                      <div className={"tw-w-[12px]"}>
                        <SVG className="d-block w-100 h-auto" src={PlusSVG} />
                      </div>
                    )}
                  </>
                }
                onClick={onNewRefinancingItemClick}
              >
                {isNewRefinancingItemOpen
                  ? t("iaq.refinancingAmounts.addMoreClose")
                  : t("iaq.refinancingAmounts.addMoreOpen")}
              </Button>
            </div>

            {isNewRefinancingItemOpen && (
              <NewRefinancingAmount
                errors={errors}
                inputChange={manageInputChange}
                onSaveClick={onSaveClick}
                t={t}
                refinancingAmount={newRefinancingItem}
              />
            )}

            {errors?.map((error, index) => (
              <Error className="mt-3" key={index}>
                {error.message}
              </Error>
            ))}
          </form>

          <FileInput
            multiple
            label={t("iaq.refinancingAmounts.attachmentText")}
            id="refinancing-amounts-attachments"
            files={files}
            postFiles={onSaveFiles}
          />

          <AnimatePresence>
            {files && !!files.length && (
              <FilesList label={t("iaq.uploadedFiles")} className="mt-4">
                {files.map((file) => (
                  <FileItem
                    file={file}
                    deleteFile={onDeleteFile}
                    isPreviewMode={isPreviewMode}
                  />
                ))}
              </FilesList>
            )}
          </AnimatePresence>

          <FieldRequestSaved fieldRequestID={fieldRequestID} />
        </>
      )}
    </>
  );
};

export default withTranslation()(RefinancingAmountForm);
